/**
 * TeamBirthdaysPanel — today's celebrants + the next 10 upcoming
 * birthdays. Same privacy convention as the staff calendar endpoint:
 * first name + last initial only.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Avatar,
  Badge,
  Card,
  Divider,
  Group,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@/components/ui';
import { IconCake } from '@tabler/icons-react';
import {
  useStaffPortalCalendarService,
  type MemberBirthdayModelView,
} from '@actschurch/ccms-sdk';
import { useEmployeeQuickView } from '../../components';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const formatBirthday = (b: MemberBirthdayModelView) =>
  `${b.birthDay} ${MONTHS[b.birthMonth - 1] ?? ''}`.trim();

export function TeamBirthdaysPanel() {
  const calendar = useStaffPortalCalendarService();
  const openEmployee = useEmployeeQuickView();

  const { data: today = [], isLoading: todayLoading } = useQuery({
    queryKey: ['hr', 'staff', 'dashboard', 'birthdays', 'today'],
    queryFn: () => calendar.getBirthdays(undefined, true),
  });
  const { data: all = [], isLoading: upcomingLoading } = useQuery({
    queryKey: ['hr', 'staff', 'dashboard', 'birthdays', 'upcoming'],
    queryFn: () => calendar.getBirthdays(),
  });

  const upcoming = useMemo(
    () =>
      (all as MemberBirthdayModelView[])
        .filter((b) => !b.isBirthdayToday)
        .sort((a, b) => a.daysUntilBirthday - b.daysUntilBirthday)
        .slice(0, 10),
    [all],
  );
  const todayList = today as MemberBirthdayModelView[];
  const isLoading = todayLoading || upcomingLoading;

  if (!isLoading && todayList.length === 0 && upcoming.length === 0) {
    return null;
  }

  return (
    // h:100% so Birthdays fills its column and matches the stacked
    // Team Leave + Policies height in the dashboard's two-up row.
    <Card withBorder padding="lg" radius="md" h="100%">
      <Group gap="xs" mb="sm">
        <ThemeIcon size="md" variant="light" color="pink" radius="xl">
          <IconCake size={16} />
        </ThemeIcon>
        <Title order={5}>Birthdays</Title>
      </Group>

      {isLoading ? (
        <Stack gap="xs">
          <Skeleton height={28} />
          <Skeleton height={28} />
        </Stack>
      ) : (
        <Stack gap="md">
          {todayList.length > 0 && (
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase">Today</Text>
              {todayList.map((b) => {
                const surname = (b as any).lastName ?? (b as any).lastInitial ?? '';
                return (
                  <Group
                    key={b.memberID}
                    wrap="nowrap"
                    gap="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => openEmployee(b.userId)}
                  >
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <Avatar src={b.avatar} size={32} radius="xl" color="pink">
                        {(b.firstName ?? '?')[0]}{(surname || '?')[0]}
                      </Avatar>
                      <Text size="sm" fw={500} truncate="end">
                        {b.firstName} {surname}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text size="xs" c="dimmed">{formatBirthday(b)}</Text>
                      <Badge size="sm" variant="light" color="pink">🎂 Today</Badge>
                    </Group>
                  </Group>
                );
              })}
            </Stack>
          )}

          {todayList.length > 0 && upcoming.length > 0 && <Divider />}

          {upcoming.length > 0 && (
            <Stack gap="xs">
              <Text size="xs" c="dimmed" tt="uppercase">Upcoming</Text>
              {upcoming.map((b) => {
                const surname = (b as any).lastName ?? (b as any).lastInitial ?? '';
                return (
                  <Group
                    key={b.memberID}
                    wrap="nowrap"
                    gap="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => openEmployee(b.userId)}
                  >
                    <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                      <Avatar src={b.avatar} size={32} radius="xl" color="pink">
                        {(b.firstName ?? '?')[0]}{(surname || '?')[0]}
                      </Avatar>
                      <Text size="sm" fw={500} truncate="end">
                        {b.firstName} {surname}
                      </Text>
                    </Group>
                    <Group gap="xs" wrap="nowrap" style={{ flexShrink: 0 }}>
                      <Text size="xs" c="dimmed">{formatBirthday(b)}</Text>
                      <Badge size="sm" variant="light" color="pink">
                        in {b.daysUntilBirthday}d
                      </Badge>
                    </Group>
                  </Group>
                );
              })}
            </Stack>
          )}
        </Stack>
      )}
    </Card>
  );
}
